import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Clock,
    User,
    Phone,
    MapPin,
    Utensils,
    Timer,
    Bike,
    AlertCircle,
    Printer,
    Edit,
    Check,
    MoreVertical,
    X,
    ChevronRight
} from 'lucide-react';
import { statusConfig } from '../hooks/useOrderActions';
import type { Order, OrderItem } from '@/types/index';
import { EditOrderDialog } from '@/components/EditOrderDialog';
import { OrderModificationHistory } from '@/components/OrderModificationHistory';
import { printReceipt, printKitchenTicket } from '@/utils/printUtils';

interface OrderCardProps {
    sessionId: string;
    sessionOrders: Order[];
    latestOrder: Order;
    isOrderUpdating: (id: string) => boolean;
    onQuickAction: (orderId: string, action: string, sessionId?: string) => void;
    onRefetch: () => void;
    currentBranch: any; // Replace with proper Branch type if available
    isStaff: boolean;
    hasNewItems?: boolean;
    onMarkItemSeen?: (sessionId: string, itemId: string) => void;
    onAssignRider?: (order: Order) => void;
    onViewDetails?: (data: any) => void;
}

export const OrderCard: React.FC<OrderCardProps> = ({
    sessionId,
    sessionOrders,
    latestOrder,
    isOrderUpdating,
    onQuickAction,
    onRefetch,
    currentBranch,
    isStaff,
    hasNewItems,
    onMarkItemSeen,
    onAssignRider,
    onViewDetails
}) => {
    const { t } = useTranslation();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editOrder, setEditOrder] = useState<any>(null);
    const [staffNotes, setStaffNotes] = useState<string>(latestOrder.notes || '');

    // Aggregate items logic
    const aggregatedItems = useMemo(() => {
        const allItems = sessionOrders.flatMap(o => o.order_items);
        const items: Record<string, {
            name: string;
            quantity: number;
            total_price: number;
            ids: string[];
            removedQuantity: number;
            removedIds: string[];
            is_acknowledged: boolean;
            unacknowledgedIds: string[];
        }> = {};

        allItems.forEach(item => {
            const key = item.menu_item_id;
            if (!items[key]) {
                items[key] = {
                    name: item.menu_items?.name || 'Unknown Item',
                    quantity: 0,
                    total_price: 0,
                    ids: [],
                    removedQuantity: 0,
                    removedIds: [],
                    is_acknowledged: true,
                    unacknowledgedIds: []
                };
            }

            if (item.is_removed) {
                items[key].removedQuantity += item.quantity;
                items[key].removedIds.push(item.id);
            } else {
                items[key].quantity += item.quantity;
                items[key].total_price += item.quantity * item.unit_price;
                items[key].ids.push(item.id);
                if (!item.is_acknowledged) {
                    items[key].is_acknowledged = false;
                    items[key].unacknowledgedIds.push(item.id);
                }
            }
        });
        return items;
    }, [sessionOrders]);

    const allItemsList = sessionOrders.flatMap(o => o.order_items);
    const activeItems = allItemsList.filter(item => !item.is_removed);
    const removedItems = allItemsList.filter(item => item.is_removed);
    const totalAmount = activeItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);

    const getOrderDuration = (createdAt: string) => {
        const now = new Date();
        const orderTime = new Date(createdAt);
        const diffMs = now.getTime() - orderTime.getTime();
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 60) {
            return `${diffMins}m`;
        } else {
            const hours = Math.floor(diffMins / 60);
            const mins = diffMins % 60;
            return `${hours}h ${mins}m`;
        }
    };

    const formatDate = (dateString: string) => new Date(dateString).toLocaleString();
    const formatPrice = (price: number) => `${currentBranch?.currency || 'USD'} ${price.toFixed(2)}`;

    const getOrderTypeColors = () => {
        if (latestOrder.order_type === 'delivery') {
            return 'bg-blue-100 border-l-4 border-l-blue-600';
        } else if (latestOrder.order_type === 'pickup') {
            return 'bg-green-100 border-l-4 border-l-green-600';
        }
        return '';
    };

    const createEditOrderObject = (latestOrder: Order, sessionOrders: Order[]) => {
        return {
            ...latestOrder,
            order_items: sessionOrders.flatMap(o => o.order_items),
            total_amount: sessionOrders.reduce((sum, o) => sum + o.total_amount, 0),
            isSessionOrder: sessionOrders.length > 1,
            sessionOrders: sessionOrders.length > 1 ? sessionOrders : undefined
        };
    };

    const getActiveAssignment = (assignments: any[] | undefined) => {
        if (!Array.isArray(assignments) || assignments.length === 0) return null;
        return assignments.find((a: any) =>
            a.assignment_status !== 'cancelled' && a.assignment_status !== 'failed'
        ) || null;
    };

    const activeAssignment = getActiveAssignment((latestOrder as any).delivery_assignments);

    // Get the next logical action for this order
    const getNextAction = () => {
        switch (latestOrder.status) {
            case 'pending':
                return { label: 'Confirm', action: 'confirm', color: 'bg-blue-500 hover:bg-blue-600' };
            case 'confirmed':
                return { label: 'Prepare', action: 'prepare', color: 'bg-orange-500 hover:bg-orange-600' };
            case 'preparing':
                return { label: 'Ready', action: 'ready', color: 'bg-green-500 hover:bg-green-600' };
            case 'ready':
                return { label: 'Complete', action: 'complete', color: 'bg-gray-600 hover:bg-gray-700' };
            default:
                return null;
        }
    };

    const nextAction = getNextAction();

    // Mobile-optimized render
    return (
        <Card className={`rounded-xl transition-all hover:shadow-lg ${getOrderTypeColors()}`}>
            {/* Mobile Layout */}
            <CardContent className="p-3 md:hidden">
                {/* Row 1: Order ID, Status, Time, Actions */}
                <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <h3 className="text-base font-bold">
                            {latestOrder.order_type === 'dine_in' && latestOrder.table_number
                                ? `T${latestOrder.table_number}`
                                : `#${latestOrder.order_number}`}
                        </h3>
                        <Badge className={`${statusConfig[latestOrder.status]?.color} text-white text-[10px] px-1.5 py-0.5`}>
                            {statusConfig[latestOrder.status]?.label}
                        </Badge>
                        {hasNewItems && (
                            <Badge variant="destructive" className="animate-pulse text-[10px] px-1.5 py-0.5">New</Badge>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 flex items-center gap-0.5">
                            <Timer className="h-3 w-3" />
                            {getOrderDuration(latestOrder.created_at || '')}
                        </span>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
                                    <Edit className="h-4 w-4 mr-2" /> Edit Order
                                </DropdownMenuItem>
                                {latestOrder.order_type === 'delivery' && onAssignRider && (
                                    <DropdownMenuItem onClick={() => onAssignRider(latestOrder)}>
                                        <Bike className="h-4 w-4 mr-2" /> Assign Rider
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => {
                                    const visibleItems = sessionOrders.flatMap(o => o.order_items).filter(oi => !oi.is_removed);
                                    const subtotal = visibleItems.reduce((sum, item) => sum + item.total_price, 0);
                                    const servicePct = Number((currentBranch as any)?.service_percentage) || 0;
                                    const taxPct = Number((currentBranch as any)?.tax_percentage) || 0;
                                    const service_amount = (subtotal * servicePct) / 100;
                                    const tax_amount = (subtotal * taxPct) / 100;
                                    const delivery_fee = latestOrder.delivery_fee || 0;
                                    printKitchenTicket({ ...latestOrder, order_items: visibleItems, subtotal_amount: subtotal, service_amount, tax_amount, delivery_fee, total_amount: subtotal + service_amount + tax_amount + delivery_fee }, { name: currentBranch?.name || "Menu Maven Restaurant" });
                                }}>
                                    <Printer className="h-4 w-4 mr-2" /> Print Kitchen
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                    const visibleItems = sessionOrders.flatMap(o => o.order_items).filter(oi => !oi.is_removed);
                                    const subtotal = visibleItems.reduce((sum, item) => sum + item.total_price, 0);
                                    const servicePct = Number((currentBranch as any)?.service_percentage) || 0;
                                    const taxPct = Number((currentBranch as any)?.tax_percentage) || 0;
                                    const service_amount = (subtotal * servicePct) / 100;
                                    const tax_amount = (subtotal * taxPct) / 100;
                                    const delivery_fee = latestOrder.delivery_fee || 0;
                                    printReceipt({ ...latestOrder, order_items: visibleItems, subtotal_amount: subtotal, service_amount, tax_amount, delivery_fee, total_amount: subtotal + service_amount + tax_amount + delivery_fee }, { name: currentBranch?.restaurants?.name || 'Menu Maven Restaurant', address: currentBranch?.address || '', phone: currentBranch?.phone || '', logo: currentBranch?.restaurants?.logo_url || undefined, currency: currentBranch?.currency || 'USD' });
                                }}>
                                    <Printer className="h-4 w-4 mr-2" /> Print Receipt
                                </DropdownMenuItem>
                                {onViewDetails && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => onViewDetails({ sessionId, sessionOrders, aggregatedItems: Object.values(aggregatedItems), latestOrder })}>
                                            <ChevronRight className="h-4 w-4 mr-2" /> View Details
                                        </DropdownMenuItem>
                                    </>
                                )}
                                {['pending', 'confirmed', 'preparing'].includes(latestOrder.status) && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem className="text-red-600" onClick={() => onQuickAction(latestOrder.id, 'cancel', sessionId)}>
                                            <X className="h-4 w-4 mr-2" /> Cancel Order
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                {/* Row 2: Customer + Type + Total */}
                <div className="flex items-center justify-between gap-2 mb-2 text-sm">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                        <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                        <span className="truncate text-gray-700">
                            {latestOrder.order_customers?.[0]?.customer_name || latestOrder.customer_name || 'Walk-in'}
                        </span>
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">
                            {latestOrder.order_type === 'delivery' && <MapPin className="h-2.5 w-2.5 mr-0.5" />}
                            {latestOrder.order_type === 'dine_in' && <Utensils className="h-2.5 w-2.5 mr-0.5" />}
                            {latestOrder.order_type === 'pickup' && <Clock className="h-2.5 w-2.5 mr-0.5" />}
                            {latestOrder.order_type?.replace('_', ' ')}
                        </Badge>
                    </div>
                    <div className="text-lg font-bold text-gray-900">{formatPrice(totalAmount)}</div>
                </div>

                {/* Row 3: Items summary */}
                <div className="text-xs text-gray-500 mb-3">
                    {activeItems.length} item{activeItems.length !== 1 ? 's' : ''}
                    {removedItems.length > 0 && <span className="text-red-500 ml-1">({removedItems.length} removed)</span>}
                    {latestOrder.order_type === 'delivery' && latestOrder.customer_address && (
                        <span className="block mt-1 text-blue-600 truncate">📍 {latestOrder.customer_address}</span>
                    )}
                </div>

                {/* Row 4: Action buttons */}
                <div className="flex gap-2">
                    {nextAction && (
                        <Button
                            className={`flex-1 h-10 text-sm font-medium text-white ${nextAction.color}`}
                            onClick={() => onQuickAction(latestOrder.id, nextAction.action, sessionId)}
                            disabled={isOrderUpdating(latestOrder.id)}
                        >
                            {nextAction.label}
                        </Button>
                    )}
                    {latestOrder.status === 'pending' && (
                        <Button
                            variant="outline"
                            className="flex-1 h-10 text-sm"
                            onClick={() => onQuickAction(latestOrder.id, 'prepare', sessionId)}
                            disabled={isOrderUpdating(latestOrder.id)}
                        >
                            Skip to Prepare
                        </Button>
                    )}
                </div>
            </CardContent>

            {/* Desktop Layout - Original */}
            <CardContent className="hidden md:block p-4 lg:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 lg:gap-6">
                    <div className="flex-1 min-w-0 space-y-3">
                        {/* Header Info */}
                        <div className="flex flex-wrap items-center gap-2 min-w-0">
                            <h3 className="text-xl font-bold truncate">
                                {latestOrder.order_type === 'dine_in' && latestOrder.table_number
                                    ? `T${latestOrder.table_number}`
                                    : `#${latestOrder.order_number}`}
                            </h3>
                            <Badge className={`${statusConfig[latestOrder.status]?.color} text-white text-xs`}>
                                {statusConfig[latestOrder.status]?.label}
                            </Badge>
                            {hasNewItems && (
                                <Badge variant="destructive" className="animate-pulse text-xs">New Items</Badge>
                            )}
                            <span className="text-sm text-gray-500 flex items-center gap-1 whitespace-nowrap">
                                <Timer className="h-3 w-3" />
                                {getOrderDuration(latestOrder.created_at || '')}
                            </span>
                        </div>

                        {/* Customer & Order Details */}
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                            <span className="flex items-center gap-2 min-w-0">
                                <Clock className="h-4 w-4" />
                                <span className="truncate">{formatDate(latestOrder.created_at || '')}</span>
                            </span>
                            
                            {/* Show all customers for table orders with multiple people */}
                            {latestOrder.order_customers && latestOrder.order_customers.length > 0 ? (
                                <div className="flex flex-col gap-1">
                                    {latestOrder.order_customers.length === 1 ? (
                                        <>
                                            <span className="flex items-center gap-2 min-w-0">
                                                <User className="h-4 w-4" />
                                                <span className="truncate">{latestOrder.order_customers[0].customer_name}</span>
                                            </span>
                                            {latestOrder.order_customers[0].customer_phone && (
                                                <Button variant="ghost" size="sm" className="h-auto p-1">
                                                    <Phone className="h-3 w-3 mr-1" />
                                                    {latestOrder.order_customers[0].customer_phone}
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        <div className="flex items-start gap-2">
                                            <User className="h-4 w-4 mt-1 flex-shrink-0" />
                                            <div className="flex flex-col gap-1">
                                                <span className="text-xs font-semibold text-gray-700">
                                                    {latestOrder.order_customers.length} Customers:
                                                </span>
                                                {latestOrder.order_customers.map((customer, idx) => (
                                                    <div key={customer.id} className="flex items-center gap-2 text-xs">
                                                        <span className="truncate">
                                                            {idx + 1}. {customer.customer_name}
                                                        </span>
                                                        {customer.customer_phone && (
                                                            <span className="text-gray-400">({customer.customer_phone})</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <span className="flex items-center gap-2 min-w-0">
                                        <User className="h-4 w-4" />
                                        <span className="truncate">{latestOrder.customer_name || 'Walk-in Customer'}</span>
                                    </span>
                                    {latestOrder.customer_phone && (
                                        <Button variant="ghost" size="sm" className="h-auto p-1">
                                            <Phone className="h-3 w-3 mr-1" />
                                            {latestOrder.customer_phone}
                                        </Button>
                                    )}
                                </>
                            )}
                            
                            <span className="flex items-center gap-2">
                                {latestOrder.order_type === 'pickup' && <Clock className="h-3 w-3" />}
                                {latestOrder.order_type === 'delivery' && <MapPin className="h-3 w-3" />}
                                {latestOrder.order_type === 'dine_in' && <Utensils className="h-3 w-3" />}
                                <span className="capitalize">{latestOrder.order_type || 'pickup'}</span>
                            </span>
                        </div>

                        {/* Delivery Specific Info */}
                        {latestOrder.order_type === 'delivery' && (
                            <div className="space-y-2">
                                {latestOrder.customer_address && (
                                    <div className="p-2 bg-blue-50 rounded text-xs text-blue-900">
                                        <span className="font-semibold">Address:</span> {latestOrder.customer_address}
                                    </div>
                                )}
                                {activeAssignment && (
                                    <div className="flex items-center gap-2">
                                        <Bike className="h-4 w-4" />
                                        <Badge variant="outline">
                                            {activeAssignment.assignment_status.replace('_', ' ')}
                                        </Badge>
                                        {activeAssignment.driver && (
                                            <span className="text-xs text-gray-500">({activeAssignment.driver.name})</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                            {latestOrder.status === 'pending' && (
                                <Button
                                    size="sm"
                                    onClick={() => onQuickAction(latestOrder.id, 'confirm', sessionId)}
                                    className="bg-blue-500 hover:bg-blue-600 text-xs whitespace-nowrap"
                                    disabled={isOrderUpdating(latestOrder.id)}
                                >
                                    Confirm
                                </Button>
                            )}
                            {['confirmed', 'pending'].includes(latestOrder.status) && (
                                <Button
                                    size="sm"
                                    onClick={() => onQuickAction(latestOrder.id, 'prepare', sessionId)}
                                    className="bg-orange-500 hover:bg-orange-600 text-xs whitespace-nowrap"
                                    disabled={isOrderUpdating(latestOrder.id)}
                                >
                                    Prepare
                                </Button>
                            )}
                            {latestOrder.status === 'preparing' && (
                                <Button
                                    size="sm"
                                    onClick={() => onQuickAction(latestOrder.id, 'ready', sessionId)}
                                    className="bg-green-500 hover:bg-green-600 text-xs whitespace-nowrap"
                                    disabled={isOrderUpdating(latestOrder.id)}
                                >
                                    Ready
                                </Button>
                            )}
                            {latestOrder.status === 'ready' && (
                                <Button
                                    size="sm"
                                    onClick={() => onQuickAction(latestOrder.id, 'complete', sessionId)}
                                    className="bg-gray-500 hover:bg-gray-600 text-xs whitespace-nowrap"
                                    disabled={isOrderUpdating(latestOrder.id)}
                                >
                                    Complete
                                </Button>
                            )}
                            {['pending', 'confirmed', 'preparing'].includes(latestOrder.status) && (
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => onQuickAction(latestOrder.id, 'cancel', sessionId)}
                                    disabled={isOrderUpdating(latestOrder.id)}
                                    className="text-xs whitespace-nowrap"
                                >
                                    Cancel
                                </Button>
                            )}

                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-xs sm:text-sm"
                                onClick={() => setEditDialogOpen(true)}
                            >
                                <Edit className="h-3 w-3" />
                                <span className="hidden sm:inline ml-1">{t('common.edit')}</span>
                            </Button>

                            {latestOrder.order_type === 'delivery' && onAssignRider && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs sm:text-sm"
                                    onClick={() => onAssignRider(latestOrder)}
                                >
                                    <Bike className="h-3 w-3" />
                                    <span className="hidden sm:inline ml-1">Rider</span>
                                </Button>
                            )}

                            <Button
                                variant="outline"
                                size="sm"
                                className="text-xs sm:text-sm"
                                onClick={() => {
                                    const visibleItems = sessionOrders.flatMap(o => o.order_items).filter(oi => !oi.is_removed)
                                    const subtotal = visibleItems.reduce((sum, item) => sum + item.total_price, 0)
                                    const servicePct = Number((currentBranch as any)?.service_percentage) || 0
                                    const taxPct = Number((currentBranch as any)?.tax_percentage) || 0
                                    const service_amount = (subtotal * servicePct) / 100
                                    const tax_amount = (subtotal * taxPct) / 100
                                    const delivery_fee = latestOrder.delivery_fee || 0
                                    const sessionOrder = {
                                        ...latestOrder,
                                        order_items: visibleItems,
                                        subtotal_amount: subtotal,
                                        service_amount,
                                        tax_amount,
                                        delivery_fee,
                                        total_amount: subtotal + service_amount + tax_amount + delivery_fee,
                                    }
                                    printKitchenTicket(sessionOrder, {
                                        name: currentBranch?.name || "Menu Maven Restaurant"
                                    })
                                }}
                            >
                                <Printer className="h-3 w-3" />
                                <span className="hidden sm:inline ml-1">Kitchen</span>
                            </Button>

                            <Button
                                variant="outline"
                                size="sm"
                                className="text-xs sm:text-sm"
                                onClick={() => {
                                    const visibleItems = sessionOrders.flatMap(o => o.order_items).filter(oi => !oi.is_removed)
                                    const subtotal = visibleItems.reduce((sum, item) => sum + item.total_price, 0)
                                    const servicePct = Number((currentBranch as any)?.service_percentage) || 0
                                    const taxPct = Number((currentBranch as any)?.tax_percentage) || 0
                                    const service_amount = (subtotal * servicePct) / 100
                                    const tax_amount = (subtotal * taxPct) / 100
                                    const delivery_fee = latestOrder.delivery_fee || 0
                                    const sessionOrder = {
                                        ...latestOrder,
                                        order_items: visibleItems,
                                        subtotal_amount: subtotal,
                                        service_amount,
                                        tax_amount,
                                        delivery_fee,
                                        total_amount: subtotal + service_amount + tax_amount + delivery_fee,
                                    }
                                    printReceipt(sessionOrder, {
                                        name: currentBranch?.restaurants?.name || 'Menu Maven Restaurant',
                                        address: currentBranch?.address || 'Restaurant Address',
                                        phone: currentBranch?.phone || 'Restaurant Phone',
                                        logo: currentBranch?.restaurants?.logo_url || undefined,
                                        currency: currentBranch?.currency || 'USD'
                                    })
                                }}
                            >
                                <Printer className="h-3 w-3" />
                                <span className="hidden sm:inline ml-1">Receipt</span>
                            </Button>

                            {onViewDetails && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-xs sm:text-sm"
                                    onClick={() => onViewDetails({
                                        sessionId,
                                        sessionOrders,
                                        aggregatedItems: Object.values(aggregatedItems),
                                        latestOrder
                                    })}
                                >
                                    Details
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Price & Items Summary */}
                    <div className="text-right space-y-2">
                        <div className="text-2xl font-bold">{formatPrice(totalAmount)}</div>
                        <div className="text-sm text-gray-500">
                            {activeItems.length} items
                            {removedItems.length > 0 && (
                                <span className="text-red-500 ml-1">({removedItems.length} removed)</span>
                            )}
                        </div>
                        <OrderModificationHistory
                            orderId={latestOrder.id}
                            onOpenChange={setEditDialogOpen}
                        />
                    </div>
                </div>

                {/* Items List */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-semibold mb-2 text-sm text-gray-700">Order Items:</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {Object.values(aggregatedItems).length === 0 ? (
                            <div className="text-sm text-gray-500 p-2">No items</div>
                        ) : (
                            Object.values(aggregatedItems).map((item, idx) => (
                                <div key={idx} className="space-y-1">
                                    {item.quantity > 0 && (
                                        <div className={`flex justify-between items-center text-sm p-2 rounded ${!item.is_acknowledged ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
                                            <span className="font-medium">{item.quantity}x {item.name}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-600">{formatPrice(item.total_price)}</span>
                                                {!item.is_acknowledged && onMarkItemSeen && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-6 w-6 p-0 hover:bg-blue-100"
                                                        onClick={() => item.unacknowledgedIds.forEach(id => onMarkItemSeen(sessionId, id))}
                                                    >
                                                        <Check className="h-4 w-4 text-blue-600" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {item.removedQuantity > 0 && (
                                        <div className="flex justify-between items-center text-sm p-2 bg-red-50 border border-red-200 rounded opacity-75">
                                            <span className="font-medium line-through text-red-600">{item.removedQuantity}x {item.name}</span>
                                            <Badge variant="destructive" className="text-xs">REMOVED</Badge>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </CardContent>

            {/* Edit Dialog - Shared between mobile and desktop */}
            <EditOrderDialog
                order={createEditOrderObject(latestOrder, sessionOrders)}
                currency={currentBranch?.currency || 'USD'}
                onOrderUpdated={onRefetch}
                open={editDialogOpen}
                setOpen={setEditDialogOpen}
            />
        </Card>
    );
};
